import { SELECTABLE_UI_LANGUAGE_CODES } from '../constants/uiLanguages';
import {
  buildHomepagePathForUiLanguage,
  isHomepageUiPath,
  normalizeRoutePath,
} from './uiRouteLanguage';

export const SITE_URL = 'https://aprendos.com';
export const DEFAULT_CANONICAL_URL = `${SITE_URL}/`;
export const BLOG_HREFLANG_LANGUAGE_CODES = ['en', 'es', 'no'];

function normalizeSeoPath(pathname) {
  const normalizedPath = normalizeRoutePath(pathname);
  if (normalizedPath.length > 1) {
    return normalizedPath.replace(/\/+$/, '');
  }
  return normalizedPath;
}

function matchBlogIndexPath(pathname) {
  const normalizedPath = normalizeSeoPath(pathname);
  if (normalizedPath === '/blog') {
    return { languageCode: 'en' };
  }

  const localizedMatch = normalizedPath.match(/^\/(en|es|no)\/blog$/);
  if (!localizedMatch) {
    return null;
  }

  return { languageCode: localizedMatch[1] };
}

function matchBlogPostPath(pathname) {
  const normalizedPath = normalizeSeoPath(pathname);
  const defaultMatch = normalizedPath.match(/^\/blog\/([^/]+)$/);
  if (defaultMatch) {
    return { languageCode: 'en', slug: defaultMatch[1] };
  }

  const localizedMatch = normalizedPath.match(/^\/(en|es|no)\/blog\/([^/]+)$/);
  if (!localizedMatch) {
    return null;
  }

  return { languageCode: localizedMatch[1], slug: localizedMatch[2] };
}

export function isBlogIndexPath(pathname) {
  return Boolean(matchBlogIndexPath(pathname));
}

export function isBlogPostPath(pathname) {
  return Boolean(matchBlogPostPath(pathname));
}

export function buildBlogIndexPath(languageCode = 'en') {
  return languageCode === 'en' ? '/blog' : `/${languageCode}/blog`;
}

export function buildBlogPostPath(slug, languageCode = 'en') {
  const safeSlug = String(slug || '').trim().replace(/^\/+|\/+$/g, '');
  if (!safeSlug) {
    return buildBlogIndexPath(languageCode);
  }

  return languageCode === 'en'
    ? `/blog/${safeSlug}`
    : `/${languageCode}/blog/${safeSlug}`;
}

export function getPrimaryBlogAlternateLanguage(languages = BLOG_HREFLANG_LANGUAGE_CODES) {
  const supportedLanguages = languages.filter((languageCode) =>
    BLOG_HREFLANG_LANGUAGE_CODES.includes(languageCode)
  );

  if (supportedLanguages.includes('en')) {
    return 'en';
  }

  return supportedLanguages[0] || 'en';
}

function normalizeBlogSlug(slug) {
  return String(slug || '').trim().replace(/^\/+|\/+$/g, '');
}

function getBlogDocumentSlug(post = {}) {
  return normalizeBlogSlug(post.slug || post.id);
}

function getSupportedAlternateSlugEntries(alternateSlugs = {}) {
  return BLOG_HREFLANG_LANGUAGE_CODES
    .map((languageCode) => [languageCode, normalizeBlogSlug(alternateSlugs?.[languageCode])])
    .filter(([, slug]) => Boolean(slug));
}

export function isPerLanguageBlogPost(post = {}) {
  return Boolean(
    post.translationGroupId
      && post.language
      && getSupportedAlternateSlugEntries(post.alternateSlugs).length > 0
  );
}

export function getBlogPostLanguage(post = {}) {
  return BLOG_HREFLANG_LANGUAGE_CODES.includes(post.language) ? post.language : '';
}

export function getBlogPostSlugForLanguage(post = {}, languageCode = 'en') {
  const requestedLanguage = BLOG_HREFLANG_LANGUAGE_CODES.includes(languageCode) ? languageCode : 'en';
  const documentSlug = getBlogDocumentSlug(post);

  if (isPerLanguageBlogPost(post)) {
    return normalizeBlogSlug(post.alternateSlugs?.[requestedLanguage]);
  }

  return documentSlug;
}

export function getBlogPostAlternateLanguages(postOrContent = {}) {
  if (isPerLanguageBlogPost(postOrContent)) {
    return getSupportedAlternateSlugEntries(postOrContent.alternateSlugs).map(([languageCode]) => languageCode);
  }

  return getAvailableBlogContentLanguages(postOrContent.content || postOrContent);
}

export function getBlogPostContentLanguage(post = {}, requestedLanguage = 'en') {
  const postLanguage = getBlogPostLanguage(post);
  if (isPerLanguageBlogPost(post) && postLanguage) {
    return postLanguage;
  }

  const availableLanguages = getAvailableBlogContentLanguages(post.content);
  if (availableLanguages.includes(requestedLanguage)) {
    return requestedLanguage;
  }

  return availableLanguages[0] || 'en';
}

export function getBlogIndexAlternateUrls(languages = BLOG_HREFLANG_LANGUAGE_CODES) {
  return languages
    .filter((languageCode) => BLOG_HREFLANG_LANGUAGE_CODES.includes(languageCode))
    .map((languageCode) => ({
      hrefLang: languageCode,
      href: `${SITE_URL}${buildBlogIndexPath(languageCode)}`,
    }));
}

export function getBlogPostAlternateUrls(slugOrPost, languages = BLOG_HREFLANG_LANGUAGE_CODES) {
  if (typeof slugOrPost === 'object' && slugOrPost !== null) {
    return getBlogPostAlternateLanguages(slugOrPost)
      .map((languageCode) => ({
        hrefLang: languageCode,
        href: `${SITE_URL}${buildBlogPostPath(getBlogPostSlugForLanguage(slugOrPost, languageCode), languageCode)}`,
      }))
      .filter((alternate) => !alternate.href.endsWith('/blog'));
  }

  return languages
    .filter((languageCode) => BLOG_HREFLANG_LANGUAGE_CODES.includes(languageCode))
    .map((languageCode) => ({
      hrefLang: languageCode,
      href: `${SITE_URL}${buildBlogPostPath(slugOrPost, languageCode)}`,
    }));
}

export function getBlogIndexXDefaultUrl(languages = BLOG_HREFLANG_LANGUAGE_CODES) {
  return `${SITE_URL}${buildBlogIndexPath(getPrimaryBlogAlternateLanguage(languages))}`;
}

export function getBlogPostXDefaultUrl(slugOrPost, languages = BLOG_HREFLANG_LANGUAGE_CODES) {
  if (typeof slugOrPost === 'object' && slugOrPost !== null) {
    const availableLanguages = getBlogPostAlternateLanguages(slugOrPost);
    const canonicalLanguage = availableLanguages.includes(slugOrPost.canonicalLanguage)
      ? slugOrPost.canonicalLanguage
      : getPrimaryBlogAlternateLanguage(availableLanguages);
    return `${SITE_URL}${buildBlogPostPath(getBlogPostSlugForLanguage(slugOrPost, canonicalLanguage), canonicalLanguage)}`;
  }

  return `${SITE_URL}${buildBlogPostPath(slugOrPost, getPrimaryBlogAlternateLanguage(languages))}`;
}

function hasBlogLanguageContent(entry = {}) {
  return ['title', 'excerpt', 'body'].some((fieldName) => {
    const value = entry?.[fieldName];
    return typeof value === 'string' && value.trim().length > 0;
  });
}

export function getAvailableBlogContentLanguages(content = {}) {
  return BLOG_HREFLANG_LANGUAGE_CODES.filter((languageCode) => hasBlogLanguageContent(content?.[languageCode]));
}

const CANONICAL_MARKETING_PATHS = new Set([
  '/',
  '/store',
  '/store/graded-readers',
  '/methodology',
  '/podcast-lessons',
  '/library',
  '/blog',
  '/meet-creator',
  '/learning-guide',
  '/learning-tips',
  '/learning-paths',
  '/faq',
  '/contact',
  '/coach',
  '/learn-english',
  '/listening-practice',
  '/privacy',
  '/terms',
  '/cookies',
]);

const LANGUAGE_SCOPED_CANONICAL_PATHS = new Set([
  '/listening-practice',
  '/privacy',
  '/terms',
  '/cookies',
  '/store',
  '/methodology',
  '/podcast-lessons',
  '/library',
  '/blog',
  '/meet-creator',
  '/learning-guide',
  '/learning-tips',
  '/learning-paths',
  '/faq',
  '/contact',
  '/coach',
]);

const LANGUAGE_LANDING_CANONICALS = new Map([
  ['/learn-english', 'en'],
  ['/learn-spanish', 'es'],
  ['/learn-norwegian', 'no'],
]);

export function getCanonicalPathForRoute(pathname) {
  const normalizedPath = normalizeSeoPath(pathname);
  const blogPostMatch = matchBlogPostPath(normalizedPath);
  if (blogPostMatch) {
    return buildBlogPostPath(blogPostMatch.slug, blogPostMatch.languageCode);
  }

  const blogIndexMatch = matchBlogIndexPath(normalizedPath);
  if (blogIndexMatch) {
    return buildBlogIndexPath(blogIndexMatch.languageCode);
  }

  if (isHomepageUiPath(normalizedPath)) {
    const segments = normalizedPath.split('/').filter(Boolean);
    if (segments.length === 0) {
      return '/';
    }

    return buildHomepagePathForUiLanguage(segments[0]);
  }

  if (CANONICAL_MARKETING_PATHS.has(normalizedPath)) {
    return normalizedPath;
  }

  const languageScopedMatch = normalizedPath.match(/^\/(en|es|no)(\/.*)$/);
  if (!languageScopedMatch) {
    return null;
  }

  const languageCode = languageScopedMatch[1];
  const canonicalPath = languageScopedMatch[2] || '/';
  const landingLanguageCode = LANGUAGE_LANDING_CANONICALS.get(canonicalPath);
  if (landingLanguageCode) {
    if (languageCode !== landingLanguageCode) {
      return null;
    }
    // English lives unprefixed (/learn-english); the others keep their prefix.
    return languageCode === 'en' ? canonicalPath : `/${languageCode}${canonicalPath}`;
  }

  if (LANGUAGE_SCOPED_CANONICAL_PATHS.has(canonicalPath)) {
    return languageCode === 'en' ? canonicalPath : `/${languageCode}${canonicalPath}`;
  }

  return null;
}

// Self-referencing canonical for a valid page with no explicit rule: the site origin
// plus the normalized pathname, without query parameters or fragments. Being outside
// the sitemap never makes a page canonicalize to the homepage. Unknown routes and
// confirmed-missing content remove the canonical instead (useNotFoundSeo).
export function buildSelfCanonicalUrl(pathname) {
  const pathOnly = String(pathname || '/').split(/[?#]/)[0] || '/';
  const normalizedPath = normalizeSeoPath(pathOnly);
  return normalizedPath === '/' ? `${SITE_URL}/` : `${SITE_URL}${normalizedPath}`;
}

export function buildCanonicalUrl(pathname) {
  const canonicalPath = getCanonicalPathForRoute(String(pathname || '/').split(/[?#]/)[0]);
  if (!canonicalPath) {
    return buildSelfCanonicalUrl(pathname);
  }

  return canonicalPath === '/' ? `${SITE_URL}/` : `${SITE_URL}${canonicalPath}`;
}

export function getHomepageAlternateUrls() {
  return SELECTABLE_UI_LANGUAGE_CODES.map((languageCode) => ({
    hrefLang: languageCode,
    href: buildCanonicalUrl(buildHomepagePathForUiLanguage(languageCode)),
  }));
}
